import React, { useState } from "react";
import { useSessionStore } from "@/stores/sessionStore";
import * as z from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

// Define the Zod validation schema
const sessionSchema = z.object({
  title: z.string().min(1, "Le titre est requis"),
  date: z.string().min(1, "La date est requise"),
  time: z.string().min(1, "L'heure est requise"),
  mentor: z.string().min(1, "Le mentor est requis"),
  status: z.enum(["Upcoming", "Completed"]),
});

type SessionFormData = z.infer<typeof sessionSchema>;

const AddEditSessionModal: React.FC = () => {
  const { modal, addSession, updateSession, closeModal } = useSessionStore();
  const [formData] = useState<SessionFormData>({
    title: modal.sessionToEdit?.title || "",
    date: modal.sessionToEdit?.date || "",
    time: modal.sessionToEdit?.time || "",
    mentor: modal.sessionToEdit?.mentor || "",
    status: modal.sessionToEdit?.status || "Upcoming",
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SessionFormData>({
    resolver: zodResolver(sessionSchema),
    defaultValues: formData,
  });

  const onSubmit = (data: SessionFormData) => {
    if (modal.type === "add") {
      addSession({ ...data, id: Date.now() });
    } else if (modal.type === "edit" && modal.sessionToEdit) {
      updateSession({ ...modal.sessionToEdit, ...data });
    }
    closeModal();
  };

  return (
    <Dialog open={modal.isOpen} onOpenChange={closeModal}>
      <DialogTrigger>Ouvrir Modal</DialogTrigger>
      <DialogContent className="max-w-md p-6 bg-white rounded-lg shadow-lg">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold text-gray-800">
            {modal.type === "edit"
              ? "Modifier la session"
              : "Ajouter une session"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Titre
            </label>
            <input
              {...register("title")}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring focus:ring-blue-500 focus:border-blue-500 p-2"
              placeholder="Titre de la session"
            />
            {errors.title && (
              <span className="text-red-500 text-sm">
                {errors.title.message}
              </span>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Date
            </label>
            <input
              {...register("date")}
              type="date"
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring focus:ring-blue-500 focus:border-blue-500 p-2"
            />
            {errors.date && (
              <span className="text-red-500 text-sm">
                {errors.date.message}
              </span>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Heure
            </label>
            <input
              {...register("time")}
              type="time"
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring focus:ring-blue-500 focus:border-blue-500 p-2"
            />
            {errors.time && (
              <span className="text-red-500 text-sm">
                {errors.time.message}
              </span>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Mentor
            </label>
            <input
              {...register("mentor")}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring focus:ring-blue-500 focus:border-blue-500 p-2"
              placeholder="Nom du mentor"
            />
            {errors.mentor && (
              <span className="text-red-500 text-sm">
                {errors.mentor.message}
              </span>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Statut
            </label>
            <select
              {...register("status")}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring focus:ring-blue-500 focus:border-blue-500 p-2"
            >
              <option value="Upcoming">À venir</option>
              <option value="Completed">Terminé</option>
            </select>
            {errors.status && (
              <span className="text-red-500 text-sm">
                {errors.status.message}
              </span>
            )}
          </div>

          <div className="flex gap-4 mt-4">
            <button
              type="submit"
              className="btn-primary bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition"
            >
              Soumettre
            </button>
            <button
              type="button"
              className="btn-secondary bg-gray-300 text-gray-800 py-2 px-4 rounded-md hover:bg-gray-400 transition"
              onClick={closeModal}
            >
              Annuler
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddEditSessionModal;
